import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Risk level mappings for filter labels
 */
const RISK_LEVEL_MAPPING = {
  1: 'active-substance|risk.Low',
  2: 'active-substance|risk.Light',
  3: 'active-substance|risk.Moderate',
  4: 'active-substance|risk.High',
  5: 'active-substance|risk.Extremely High',
};

/**
 * Risk filter keys that should use risk level mapping
 */
const RISK_FILTER_KEYS = [
  'acute_toxicity',
  'long_term_effects',
  'fish_or_daphnia', 
  'birds',
  'bees',
  'persistence',
  'leaching_potential'
];

/**
 * Custom hook to manage filter labels in a search application.
 */
export const useFilterLabels = () => {
  const [labels, setLabels] = useState({});
  const { t } = useTranslation();

  /**
   * Function to set a label for a specific filter value.
   * @type {(function(*, *, *): void)|*}
   */
  const setFilterLabel = useCallback((filterKey, valueId, label) => {
    setLabels((prev) => ({
      ...prev,
      [filterKey]: {
        ...prev[filterKey],
        [valueId]: label,
      },
    }));
  }, []);

  /**
   * Function to get the label for a specific filter value.
   * @type {Function}
   */
  const getFilterLabel = useCallback(
    (filterKey, valueId) => {
      // Check if we have a stored label
      const storedLabel = labels[filterKey]?.[valueId];
      if (storedLabel) {
        return storedLabel;
      }

      // For risk filter keys, map numeric values to French labels
      if (RISK_FILTER_KEYS.includes(filterKey)) {
        const riskLevelKey = RISK_LEVEL_MAPPING[valueId];
        if (riskLevelKey) {
          return t(riskLevelKey);
        }
      }

      return valueId;
    },
    [labels, t]
  );

  /**
   * Function to clear all filter labels.
   * @type {(function(): void)|*}
   */
  const clearFilterLabels = useCallback(() => {
    setLabels({});
  }, []);

  return {
    setFilterLabel,
    getFilterLabel,
    clearFilterLabels,
    labels,
  };
};
